import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { PlayCircle, ArrowRight, ExternalLink } from "lucide-react";
import { IMAGES, PRODUCT } from "./data";
import MagicSparkles from "./MagicSparkles";
import { LANDING } from "@/constants/testIds";
import { track } from "@/lib/tracking";

const VIDEO_ID = "Db_IJi5ZQog";
const VIDEO_THUMB = `https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`;
// `enablejsapi=1` lets us listen for play/progress events.
const VIDEO_EMBED = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`;
const VIDEO_WATCH = `https://youtu.be/${VIDEO_ID}`;

function loadYouTubeAPI() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const existing = document.getElementById("yt-iframe-api");
    if (!existing) {
      const tag = document.createElement("script");
      tag.id = "yt-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
  });
}

export default function Hero() {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const progressRef = useRef(new Set());

  useEffect(() => {
    if (!playing || !iframeRef.current) return;
    let cancelled = false;
    let interval = null;

    (async () => {
      const YT = await loadYouTubeAPI();
      if (cancelled || !iframeRef.current) return;
      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              track("vsl_play", { video_id: VIDEO_ID });
            }
          },
        },
      });
      interval = setInterval(() => {
        const p = playerRef.current;
        if (!p || !p.getCurrentTime || !p.getDuration) return;
        const dur = p.getDuration();
        const cur = p.getCurrentTime();
        if (!dur) return;
        const pct = Math.round((cur / dur) * 100);
        for (const t of [25, 50, 75, 100]) {
          if (pct >= t && !progressRef.current.has(t)) {
            progressRef.current.add(t);
            track("vsl_progress", { percent: t, video_id: VIDEO_ID });
          }
        }
      }, 1000);
    })();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [playing]);

  return (
    <section
      id="top"
      className="relative section-y pt-14 lg:pt-20 overflow-hidden"
      style={{
        background:
          "radial-gradient(120% 80% at 20% 0%, #ebe1f2 0%, #f3ecf7 45%, #f7ede0 100%)",
      }}
    >
      {/* Ambiência noturna suave */}
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#d4c4e8] opacity-50 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-32 w-[460px] h-[460px] rounded-full bg-[#fce0db] opacity-40 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 stars-bg opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        {/* Mobile-first: headline → video → CTA stack.
            Desktop: 2-column with headline (top-left) / CTA (bottom-left) / video (full right). */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-14 lg:items-center">
          {/* A — HEADLINE block */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-1 lg:row-start-1 lg:col-start-1 max-w-xl"
          >
            <span
              data-testid={LANDING.heroBadge}
              className="eyebrow mb-5 lg:mb-7"
            >
              Construindo a autonomia do seu bebê
            </span>

            <h1
              data-testid={LANDING.heroHeadline}
              className="font-display text-[32px] leading-[1.08] sm:text-5xl lg:text-[58px] lg:leading-[1.05] text-[#1f1430]"
            >
              Toda mãe exausta
              <span className="block">já tentou de tudo,</span>
              <span className="block text-[#6d28d9] mt-1">menos isso.</span>
            </h1>
          </motion.div>

          {/* B — VIDEO (mobile: between headline & CTA; desktop: full right column) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="order-2 lg:order-2 lg:row-start-1 lg:row-span-2 lg:col-start-2"
          >
            <div
              data-testid={LANDING.heroVideoPlaceholder}
              className="relative aspect-video rounded-3xl overflow-hidden bg-[#2d1f43] border border-[#d4c4e8] group glow-violet"
            >
              {playing ? (
                <iframe
                  ref={iframeRef}
                  src={VIDEO_EMBED}
                  title="Jornada do Sono — Mariana Mello"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 w-full h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6d28d9]"
                  aria-label="Assistir ao vídeo da Mariana"
                >
                  <img
                    src={VIDEO_THUMB}
                    alt="Vídeo da Jornada do Sono"
                    onError={(e) => {
                      e.currentTarget.src = IMAGES.sleepingBaby;
                    }}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1f1430]/65 via-[#1f1430]/10 to-transparent" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <span className="flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white/95 shadow-[0_15px_40px_-10px_rgba(109,40,217,0.5)] mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300">
                      <PlayCircle
                        className="w-9 h-9 lg:w-12 lg:h-12 text-[#6d28d9]"
                        strokeWidth={1.5}
                      />
                    </span>
                    <p className="font-display text-xl lg:text-2xl text-white">
                      Assista o vídeo da Mariana
                    </p>
                    <p className="text-xs lg:text-sm text-white/85 mt-1.5 lg:mt-2 font-body">
                      Por que seu bebê não dorme, e como mudar isso
                    </p>
                  </div>
                </button>
              )}
            </div>
            <a
              href={VIDEO_WATCH}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#7a6b8c] hover:text-[#6d28d9] transition px-1 font-body"
            >
              <ExternalLink className="w-3 h-3" />
              <span>Preferir assistir direto no YouTube? Clique aqui</span>
            </a>
          </motion.div>

          {/* C — CTA + SUBHEADLINE (mobile: after video; desktop: under headline on left) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="order-3 lg:order-3 lg:row-start-2 lg:col-start-1 max-w-xl"
          >
            <p className="text-[15px] lg:text-lg text-[#4a3a5e] leading-relaxed mb-6 font-body max-w-md">
              Quando o problema não é o bebê, e sim o que ninguém te ensinou
              sobre como o sono infantil funciona, uma noite inteira deixa de
              ser sonho e vira rotina.
            </p>

            <a
              href="#entregaveis"
              data-testid={LANDING.heroCta}
              className="group relative btn-shine btn-jelly cta-pulse cta-primary inline-flex w-full sm:w-auto justify-center"
            >
              <MagicSparkles />
              <span className="relative z-10">Quero ver o conteúdo completo</span>
              <ArrowRight className="w-4 h-4 relative z-10 arrow-slide" />
            </a>
            <p className="text-xs text-[#7a6b8c] font-body mt-4 text-center sm:text-left">
              6 módulos, 21 aulas em vídeo, em ordem de aplicação.
            </p>
          </motion.div>
        </div>

        {/* Bullets centered BELOW the main block */}
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-14 lg:mt-20 mx-auto max-w-5xl grid sm:grid-cols-3 gap-6 lg:gap-8"
        >
          {[
            "Você está no limite. Cada noite interrompida custa sua energia e o prazer da maternidade.",
            'A culpa de "ter acostumado mal" paralisa mais mães do que o próprio cansaço. Entender a causa real liberta.',
            "Dormir não é sorte nem esperar crescer. É uma habilidade que seu filho desenvolve em semanas.",
          ].map((t, i) => (
            <li
              key={i}
              className="relative flex flex-col items-center text-center gap-3 px-3"
            >
              <span className="h-2 w-2 rounded-full bg-[#6d28d9]" />
              <span className="text-[#3f3651] text-[14px] lg:text-[15px] leading-relaxed font-body">
                {t}
              </span>
            </li>
          ))}
        </motion.ul>

        {/* Construção do dia inteiro — explicação do "isso" da headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mt-16 lg:mt-24 mx-auto max-w-3xl rounded-3xl bg-white/55 border border-violet-200/60 backdrop-blur-sm px-7 py-10 lg:px-12 lg:py-14 shadow-[0_30px_80px_-30px_rgba(124,58,237,0.18)]"
        >
          <span className="block text-[11px] uppercase tracking-[0.22em] text-[#6d28d9] font-bold mb-5 text-center">
            Antes que você ache que é só mais uma dica
          </span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-[34px] text-[#1f1430] leading-snug text-center mb-7">
            Não é só ajustar a noite.
            <span className="block text-[#6d28d9] font-medium mt-1">
              É construir o dia inteiro.
            </span>
          </h2>
          <div className="space-y-5 text-[#3f3651] text-[15px] lg:text-[16px] leading-[1.8] font-body">
            <p>
              A maioria das mães tenta resolver o sono do bebê apenas quando o
              quarto escurece. Pegam dicas isoladas, seguem sequências prontas,
              mudam a rotina da noite… e nada funciona de verdade.
            </p>
            <p>
              O que ninguém conta é que{" "}
              <span className="font-semibold text-[#1f1430]">
                autonomia de sono não acontece. Ela é construída
              </span>{" "}
              — tijolo por tijolo, desde o momento em que o bebê abre os olhos
              de manhã.
            </p>
            <p>
              Cada detalhe do dia, na ordem certa e do jeito certo, faz parte
              dessa construção. E quando tudo se encaixa, a noite finalmente
              muda.
            </p>
            <p>
              Na Jornada do Sono você aprende cada ajuste dessa construção,
              passo a passo, do amanhecer até a hora de dormir — em um método
              que respeita o seu bebê e cabe na sua rotina.
            </p>
            <p className="text-[#6d28d9] font-medium">
              Porque mãe exausta não precisa de mais uma dica solta.{" "}
              <span className="font-semibold">
                Precisa entender a construção completa.
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
